/**
 * UserDashboardContainer
 * --
 */
import React from 'react';
import UserDashboardPresenter from './UserDashboardPresenter';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useBulkUpdateUserGrade,
  useBulkUpsertUser,
  useGetUsers,
  userQueryKeys,
  useUpdateUser,
  useDeleteUser,
} from 'hooks/UserHooks';
import { useGetGrades } from 'hooks/GradeHooks';
import MessageAlert from 'utils';
import {
  useBulkCreateCouponUser,
  useUpdateCouponUser,
} from 'hooks/CouponUserHooks';
import Formatter from 'utils/Formatter';
import {
  useUpsertPointHistory,
  useUsePointHistory,
} from 'hooks/PointHistoryHooks';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { useGetSetting } from 'hooks/SettingHooks';
import {
  useCreateMemberShipGrade,
  useDeleteMemberShipGrade,
} from 'hooks/MemberShipGradeHooks';
import { useGetStatisticsSummary } from 'hooks/StatisticsHooks';
import { useGetCoupons } from 'hooks/CouponHooks';
import { useGetProductPolicies } from 'hooks/ProductPolicyHooks';
import { encrypt } from 'utils/Crypto';
import Logger from 'utils/logger';
import { ACCOUNT_STATUS } from 'constants/UserContants';
import * as XLSX from 'xlsx/xlsx.mjs';

const logger = new Logger('UserDashboardContainer');

const INITIAL_SEARCH_VALUE = {
  page: 1,
  expand: 'N',
  count: 10,
};

const UserDashboardContainer = (props) => {
  /* ===== ROUTE ===== */
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 파라미터 추출
  const modal = searchParams.get('modal') ?? null;

  /* ===== Mutate ===== */
  const queryClient = useQueryClient();
  const { updateUser } = useUpdateUser();
  const { updateCouponUser } = useUpdateCouponUser();
  const { usedPointHistory } = useUsePointHistory();

  const { bulkUpsertUser } = useBulkUpsertUser();
  const { bulkUpdateUserGrade } = useBulkUpdateUserGrade();
  const { upsertPointHistory } = useUpsertPointHistory();

  const { bulkCreateCouponUser } = useBulkCreateCouponUser();

  const { createMemberShipGrade } = useCreateMemberShipGrade();
  const { deleteMemberShipGrade } = useDeleteMemberShipGrade();

  const { deleteUser } = useDeleteUser();

  /* ===== STATE ===== */
  const [searchValue, setSearchValue] = React.useState(INITIAL_SEARCH_VALUE);
  const [searchInputValue, setSearchInputValue] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [detailUser, setDetailUser] = React.useState(modal);

  // 선택회원
  const [selectedUsers, setSelectedUsers] = React.useState([]);

  // 구매건수, 금액 검색 기간
  const [period, setPeriod] = React.useState('7-day');
  // 선택한 플렛폼
  const [selecetedPlatform, setSelecetedPlatform] = React.useState('all');

  /* ===== Data ===== */
  const userQueryKey = userQueryKeys.getUsers();
  // 유저 전체 조회
  const { data: users, refetch } = useGetUsers();
  // 등급 전체 조회
  const { data: grades } = useGetGrades();
  // 등급설정 조회
  const { data: gradeSetting } = useGetSetting(1, null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;

      return data?.grade_setting;
    },
    initialData: null,
  });
  // 구매 건수/금액
  const { data: statistics, isLoading: statisticsLoading } =
    useGetStatisticsSummary(
      'users-purchase',
      {
        endDate: dayjs().format('YYYY-MM-DD'),
        startDate: dayjs()
          .subtract(period.split('-')[0], period.split('-')[1])
          .format('YYYY-MM-DD'),
      },
      {
        select: (res) => {
          const { status, data } = res;

          if (status !== 200) return [];

          return data;
        },
        enabled: !!period,
        initialData: [],
      },
    );
  // 쿠폰 목록 조회
  const { data: CouponList } = useGetCoupons(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      const payment = ['MANUAL', 'DOWNLOAD'];
      const filtered = data?.filter((_item) =>
        payment.includes(_item?.payment_type),
      );
      return filtered;
    },
    initialData: [],
  });
  // 쿠폰 정책 조회
  const { data: CouponPolicy } = useGetProductPolicies(
    {
      policy_type: 'COUPON',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        const filtered = data.filter((_item) => _item?.is_show === 'Y');

        return filtered;
      },
      initialData: [],
    },
  ); /* ===== PROPS ===== */

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string | number | boolean} v
   * @param {func} callback
   */
  const handleChange = (t, v, callback = null) => {
    setSearchInputValue((prevState) => {
      const newData = { ...prevState, [t]: v };

      if (callback) callback(v);

      return newData;
    });
  };

  /**
   * 회원 업데이트
   * --
   * @param {account_status | grade_id} t
   * @param {number} id
   * @param {object} obj
   * @param {func} callback
   */
  const handleSubmit = async (t, id, obj, callback) => {
    try {
      const mutate = t === 'account_status' ? updateUser : bulkUpdateUserGrade;

      const body =
        t === 'account_status'
          ? {
              user_id: id,
              ...obj,
            }
          : {
              grade_id: id,
              grade_reason: '관리자 수동 변경',
              ...obj,
            };

      const { status, data } = await mutate(body);

      if (status !== 200) {
        MessageAlert.warning('업데이트에 실패하였습니다.');
        return;
      }

      MessageAlert.success('성공적으로 업데이트 되었습니다!');
      callback && callback(data);
    } catch (err) {
      console.error('[handleSubmit] Error!! : ', err);
      MessageAlert.error('메모 업데이트 중 오류가 발생하였습니다!');
      throw err;
    }
  };

  /**
   * 회원 쿠폰 사용처리
   * --
   */
  const handleUsedCoupon = async (idx) => {
    try {
      const confirm = window.confirm('해당 쿠폰을 사용처리하시겠습니까?');

      if (!confirm) {
        MessageAlert.info('취소되었습니다.');
        return;
      }

      const { status } = await updateCouponUser({
        coupon_user_id: idx,
        use_date: Formatter.dateToUnix(),
      });

      if (status !== 200) {
        MessageAlert.warning('쿠폰 사용처리에 실패하였습니다.');
        return;
      }

      MessageAlert.success('해당 쿠폰은 사용처리되었습니다.');
      window.location.reload();
    } catch (err) {
      console.error('[handleUsedCoupon] Error!! : ', err);
      MessageAlert.error('쿠폰 사용처리에 실패하였습니다.');
      throw err;
    }
  };

  /**
   * 검색 필터
   * --
   */
  const handleSearch = () => {
    const sv = { ...searchInputValue };

    const {
      countStart,
      countEnd,
      priceStart,
      priceEnd,
      pricePlatform,
      countPlatform,
    } = searchInputValue;

    // 구매건수, 구매금액 검색 예외처리
    if ((countStart || countEnd) && !countPlatform) {
      MessageAlert.info('구매 건수를 검색하실 플랫폼을 선택해주세요!');
      return;
    }

    if ((priceStart || priceEnd) && !pricePlatform) {
      MessageAlert.info('구매 금액을 검색하실 플랫폼을 선택해주세요!');
      return;
    }

    if (countPlatform && !countStart && !countEnd) {
      searchValue['countPlatform'] = null;
    }

    if (pricePlatform && !priceStart && !priceEnd) {
      sv['pricePlatform'] = null;
    }
    // 구매건수, 구매금액 검색 예외처리 끝

    const mergedParams = Object.entries({
      ...Object.fromEntries(searchParams.entries()), // 기존 searchParams 객체화
      ...sv, // 새로운 입력값 병합
      // ...query,
    }).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value; // null이나 빈 문자열이 아닌 값만 추가
      }
      return acc;
    }, {});

    setSearchValue((prev) => ({
      ...prev,
      ...mergedParams,
    }));

    // // 병합된 객체를 쿼리 스트링으로 변환
    // const newSearchParams = new URLSearchParams(mergedParams).toString();

    // setSearchParams(newSearchParams, { replace: true });
  };

  /**
   * 회원 적립금
   * --
   * @param {string} type EARNED | DEDUCTED | MULTI-EARNED | MULTI-DEDUCTED
   * @param {object} data
   * @param {function} callback
   */
  const handleReservePoint = async (type, data, callback) => {
    if (!data) return;

    const submitType = type.toLocaleUpperCase();

    const confirm = (msg = '') => window.confirm(msg);

    /**
     * 적립금 지급 유효성 검사
     * @returns {boolean}
     */
    const earnedValidateCheck = (obj) => {
      if (!obj.point) {
        MessageAlert.info('지급하실 적립금을 입력해주세요.');
        return false;
      }

      if (!obj.expired_date) {
        MessageAlert.info('적립금 유효기간을 입력해주세요.');
        return false;
      }

      if (!obj.memo) {
        MessageAlert.info('적립 사유를 입력해주세요.');
        return false;
      }

      return true;
    };

    let res;
    switch (submitType) {
      /**NOTE - 적립
       * user_id : number
       * point : number
       * expired_date : number
       * memo : string
       */
      case 'EARNED':
        if (!confirm(`${data.point}P만큼 적립하시겠습니까?`)) return false;

        if (!earnedValidateCheck(data)) return false;

        res = await upsertPointHistory([
          {
            ...data,
            expired_date: dayjs().add(data.expired_date, 'day').unix(),
            shop_information_id: 1,
            memo: data?.memo,
            type: 'EARNED',
          },
        ]);

        if (res.status !== 200) {
          MessageAlert.info('적립금 지급에 실패하였습니다.');
          return false;
        }

        MessageAlert.success('적립금이 지급되었습니다.');
        callback((prev) => ({
          ...prev,
          totalPoint: prev.totalPoint + data.point,
        }));
        return true;

      /**NOTE - 차감
       * user_id : number
       * point: number
       * memo: string
       */
      case 'DEDUCTED':
        if (!data.point) {
          MessageAlert.info('차감하실 적립금을 입력해주세요.');
          return;
        }

        if (!confirm(`${data.point}P만큼 차감하시겠습니까?`)) return;

        res = await usedPointHistory({
          user_id: data.user_id,
          use_point: data.point,
          memo: data?.memo,
        });

        if (res.status !== 200) {
          MessageAlert.info('적립금 차감에 실패하였습니다.');
          return;
        }

        MessageAlert.success('적립금이 차감되었습니다.');
        callback((prev) => ({
          ...prev,
          totalPoint: prev.totalPoint - data.point,
        }));
        return true;

      /**NOTE - 적립금 멀티 지급
       * users : array
       * point : number
       * expired_date : number
       * memo : string
       */
      case 'MULTI-EARNED':
        if (
          !confirm(
            `${data?.users?.length}명에게 ${data.point}P만큼 적립하시겠습니까?`,
          )
        )
          return false;

        if (!earnedValidateCheck(data)) return false;

        const bulkData = data?.users?.map((user) => ({
          user_id: user?.key,
          point: data?.point,
          expired_date: dayjs().add(data.expired_date, 'day').unix(),
          memo: data?.memo,
          shop_information_id: 1,
          type: 'EARNED',
        }));

        res = await upsertPointHistory(bulkData);

        if (res.status !== 200) {
          MessageAlert.info('적립금 지급에 실패하였습니다.');
          return false;
        }

        setSelectedUsers([]);
        MessageAlert.success('적립금이 지급되었습니다.');
        return true;

      default:
        return false;
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedUsers((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedUsers((prev) =>
          prev.filter((select) => select.key !== obj.key),
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedUsers(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedUsers.filter((user) =>
          obj.some((ob) => ob.key === user.key),
        );

        setSelectedUsers(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedUsers.length === obj.length;

        if (!isAll) {
          setSelectedUsers(obj);
        } else {
          setSelectedUsers([]);
        }
        return;
      default:
        return;
    }
  };

  /**
   * 회원 Bulk 업데이트
   * --
   * @param {string} key
   * @param {*} value
   */
  const handleBulkUsers = async (key, value) => {
    if (!selectedUsers?.length) {
      MessageAlert.info('회원을 선택해주세요!');
      return;
    }

    const confirm = (msg = '') => window.confirm(msg);

    try {
      if (
        !confirm(`${selectedUsers?.length}명의 회원 상태를 변경하시겠습니까?`)
      )
        return;

      setIsLoading(true);

      const newArr = selectedUsers?.map((_item) => ({
        user_id: _item?.key,
        [key]: value,
      }));
      const { status } = await bulkUpsertUser(newArr);

      if (status !== 200) {
        MessageAlert.info('회원 수정에 실패하였습니다.');
        return;
      }

      queryClient.invalidateQueries({
        userQueryKey,
      });
      MessageAlert.success(
        `${selectedUsers?.length}명의 회원의 상태가 성공적으로 변경되었습니다.`,
      );
    } catch (err) {
      console.error('[handleBulkUsers] Error!! ', err);
      MessageAlert.error('상태변경에 실패하였습니다!');
      return false;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /**
   * 등급 일괄 수정
   * --
   * @param {object} data { grade_id, grade_reason, grade_name }
   */
  const handleUpdateGrade = async (data) => {
    if (!selectedUsers?.length) {
      MessageAlert.info('변경할 회원을 선택해주세요!');
      return false;
    }

    const { grade_name, ...rest } = data;
    const confirm = window.confirm(
      `${selectedUsers?.length}명의 회원을 ${grade_name}등급으로 변경하시겠습니까?`,
    );

    if (!confirm) return;

    try {
      setIsLoading(true);

      const bulkData = selectedUsers?.map((_item) => ({
        user_id: _item?.key,
      }));

      const { status } = await bulkUpdateUserGrade({
        ...rest,
        user_ids: bulkData,
      });

      if (status !== 200) {
        MessageAlert.info('등급 변경에 실패하였습니다.');
        return false;
      }

      queryClient.invalidateQueries({
        userQueryKey,
      });
      MessageAlert.success(
        `${selectedUsers?.length}명의 회원이 성공적으로 ${grade_name}으로 변경되었습니다.`,
      );
      setSelectedUsers([]);
      return true;
    } catch (err) {
      console.error('[handleUpdateGrade] Error!! ', err);
      MessageAlert.error('등급 변경에 실패하였습니다!');
      return false;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /**
   * 비즈니스 회원 전환
   * --
   * @param {object} obj
   * @param {func} callback
   */
  const handleApproveBusiness = async (obj, callback) => {
    if (!businessGrade) {
      window.alert(
        '비즈니스 등급이 설정되어있지 않습니다.\n"사이트 관리 > 정책 관리 > 등급 관리"에서 설정 후 전환해주세요.',
      );
      return;
    }

    const confirm = (msg = '') => window.confirm(msg);

    if (
      !confirm(
        `사업자 회원으로 승인하시겠습니까?\n승인 시 ${businessGrade?.grade_name} 등급으로 자동 전환됩니다.`,
      )
    )
      return;

    try {
      setIsLoading(true);

      const gradeData = {
        grade_id: businessGrade?.grade_id,
        user_id: obj?.user_id,
        grade_reason: '사업자 회원 전환',
      };

      // 사업자 등급 변경 내역 생성
      const { status: membershipStatus, data: membershipData } =
        await createMemberShipGrade(gradeData);

      if (membershipStatus !== 200) {
        MessageAlert.info('사업자 회원 전환에 실패하였습니다.');
        return;
      }

      const userUpdateData = {
        user_id: obj?.user_id,
        business_level: 2,
        grade_id: businessGrade?.grade_id,
      };

      // 회원 업데이트
      const { status, data } = await updateUser(userUpdateData);

      if (status !== 200) {
        MessageAlert.info('사업자 회원 전환에 실패하였습니다.');
        // 실패 시 내역 삭제 처리
        await deleteMemberShipGrade(membershipData?.membership_grade_id);
        return;
      }

      MessageAlert.success('사업자 회원으로 전환되었습니다.');
      callback && callback(data);
    } catch (err) {
      console.error('[handleApproveBusiness] Error!! ', err);
      MessageAlert.error('비즈니스 회원 승인 중 오류가 발생하였습니다!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /**
   * 회원 탈퇴
   * --
   * @param {number} idx 회원 id
   */
  const handleDeleteUser = async (idx) => {
    try {
      if (!idx) return MessageAlert.error('회원 정보를 찾을 수 없습니다,');
      const confirm = (msg = '') => window.confirm(msg);
      if (
        !confirm(
          '정말 회원을 탈퇴 처리 하시겠습니까?\n탈퇴 처리 후 복구 할 수 없습니다.',
        )
      )
        return;

      setIsLoading(true);

      const { status } = await deleteUser(idx);

      if (status === 200) {
        MessageAlert.success('회원 탈퇴 처리에 성공하였습니다.');
        queryClient.invalidateQueries({
          userQueryKey,
        });
      }
    } catch (e) {
      console.error('[handleDeleteUser] Error!! ', e);
      MessageAlert.error('탈퇴 처리에 실패하였습닝다.');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /**
   * 쿠폰 발급
   * --
   * @param {number} idx 쿠폰 id
   * @param {func} callback
   */
  const handleIssueCoupon = async (idx, num, callback) => {
    let isSuccess = false;

    if (!idx) {
      MessageAlert.info('발급할 쿠폰을 선택해주세요!');
      return;
    }

    if (!num) {
      MessageAlert.info('발급할 수량을 입력해주세요!');
      return;
    }

    const confirm = window.confirm(
      `${selectedUsers?.length} 명에게 쿠폰을 ${num}장 발급하시겠습니까?`,
    );

    if (!confirm) return;

    try {
      setIsLoading(true);
      const issueData = {
        user_ids: selectedUsers?.map((user) => ({ user_id: user?.userId })),
        coupon_id: idx,
        per_user_limit: num ?? 1,
      };

      const { status, data } = await bulkCreateCouponUser(issueData);

      if (status !== 200) {
        const koreanOnly = data.replace(/[^가-힣]/g, '');
        MessageAlert.info(koreanOnly);
        return;
      }

      MessageAlert.success(
        `${selectedUsers?.length} 명에게 쿠폰이 발급되었습니다.`,
      );
      isSuccess = true;
    } catch (err) {
      console.error('[handleIssueCoupon] Error!! ', err);
      MessageAlert.error('쿠폰 발급에 실패하였습니다!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);

        if (isSuccess && callback) {
          callback();
        }
      }, 1000);
    }
  };

  /**
   *
   * @param {string} key
   * @param {any} value
   */
  const handleUpdateQuery = (key, value) => {
    if (key === 'reset') {
      setSearchValue(INITIAL_SEARCH_VALUE);
      setSearchInputValue({});
      setSelecetedPlatform('all');
    } else {
      setSearchValue((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  /**
   * 회원 상세모달 핸들러
   * --
   * @param {number | null} uid
   */
  const handleDetailModal = (uid = null) => {
    if (uid) {
      const encode = encodeURIComponent(encrypt(uid));
      setDetailUser(encode);
    } else {
      setDetailUser(null);
    }
  };

  /**
   * 선택회원 엑셀 다운로드
   * --
   */
  const handleXlsxDownload = () => {
    if (!selectedUsers.length) {
      MessageAlert.info('다운로드할 회원을 선택해주세요!');
      return;
    }

    let isSuccess = false;

    try {
      setIsLoading(true);

      const xlsxData = selectedUsers.reduce((acc, cur) => {
        const d = {
          이름: cur.name,
          아이디: cur.account,
          연락처: cur.phone,
          이메일: cur.email,
          등급: cur.grade,
          성별: cur.gender,
          나이: cur.age,
          지역: cur.area,
          회원가입일: Formatter.unixToDate(cur.created_at, 'YYYY-MM-DD'),
          가입경로: cur.path,
          계정상태: ACCOUNT_STATUS[cur.status].label,
          최근접속일: Formatter.unixToDate(cur.accessTime, 'YYYY-MM-DD'),
          '총 구매 건수': cur.totalCount,
          '총 구매 금액': cur.totalPrice,
          '쇼핑몰 구매 건수': cur.totalShopOrderCount,
          '쇼핑몰 구매 금액': cur.totalShopOrderPrice,
          '오더 구매 건수': cur.totalOrderCount,
          '오더 구매 금액': cur.totalOrderPrice,
        };

        acc.push(d);

        return acc;
      }, []);

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `회원목록 ${dayjs().format('YYYYMMDDHHmmss')}.xlsx`);
      isSuccess = true;
    } catch (err) {
      logger.error('handleXlsxDownload', err);
    } finally {
      setTimeout(() => {
        if (isSuccess) {
          MessageAlert.success('다운로드 성공');
        } else {
          MessageAlert.warning('다운로드 실패');
        }

        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 유저 데이터 정제
   * --
   */
  const userData = React.useMemo(() => {
    // 유저 + 등급 + 주문내역 정제
    const newUsers = users?.data?.map((user) => ({
      ...user,
      grade: grades?.data?.find((grade) => grade?.grade_id === user?.grade_id),
      ...(statistics && statistics?.length
        ? statistics?.find((_item) => _item?.user_id === user?.user_id)
        : {}),
    }));

    return newUsers;
  }, [users?.data, grades?.data, statistics]);

  /**
   * 발급 가능 쿠폰 정제
   * --
   */
  const CouponData = React.useMemo(() => {
    if (!CouponList.length || !CouponPolicy.length) return [];

    // 쿠폰 + 정책
    const newCouponList = CouponList?.map((_item) => ({
      ..._item,
      key: _item?.coupon_id,
      product_policy:
        CouponPolicy?.find((_sub) => _sub?.coupon_id === _item?.coupon_id) ??
        null,
    }));

    // 정책이 없는 쿠폰(발행 불가한 쿠폰) 필터
    const filterCouponList = newCouponList?.filter(
      (_item) => _item?.product_policy,
    );

    return filterCouponList;
  }, [CouponList, CouponPolicy]);

  /**
   * 비즈니스 등급 추출
   * --
   */
  const businessGrade = React.useMemo(() => {
    if (!gradeSetting || !grades?.data?.length) return null;

    const findBusinessGrade = grades?.data?.find(
      (grade) => grade?.grade_id === gradeSetting?.business_grade_id,
    );

    if (!findBusinessGrade) return null;

    return findBusinessGrade;
  }, [gradeSetting, grades?.data]);

  /**
   * 검색 기간 변경 시 로딩 처리
   * --
   */
  React.useEffect(() => {
    if (statisticsLoading) {
      setIsLoading(true);

      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [statisticsLoading]);

  const consoleDatas = () => {
    const findUser = userData.find(
      (userInfo) => userInfo.user_name === '김영철',
    );
    const findStatistics = statistics.filter(
      (data) => data.user_id === findUser.user_id,
    );
    const checkDate = dayjs()
      .subtract(period.split('-')[0], period.split('-')[1])
      .format('YYYY-MM-DD');
    return checkDate;
  };
  // console.log(consoleDatas());
  /* ===== RENDER ===== */
  return (
    <>
      <UserDashboardPresenter
        users={userData}
        grades={grades?.data}
        modal={detailUser}
        page={searchValue.page ?? 1}
        count={searchValue.count ?? 10}
        expand={searchValue.expand ?? 'N'}
        onUpdateQuery={handleUpdateQuery}
        onChange={handleChange}
        searchInputValue={searchInputValue}
        onSubmit={handleSubmit}
        onSearch={handleSearch}
        // parameter={parameter}
        parameter={searchValue}
        navigate={navigate}
        onUsedCoupon={handleUsedCoupon}
        location={location}
        onReservePoint={handleReservePoint}
        selected={selectedUsers}
        onSelect={handleSelect}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        onBulk={handleBulkUsers}
        onUpdateGrade={handleUpdateGrade}
        onApprove={handleApproveBusiness}
        period={period}
        setPeriod={setPeriod}
        selecetedPlatform={selecetedPlatform}
        setSelecetedPlatform={setSelecetedPlatform}
        coupon={CouponData}
        onIssueCoupon={handleIssueCoupon}
        onDetailModal={handleDetailModal}
        onXlsxDownload={handleXlsxDownload}
        onDeleteUser={handleDeleteUser}
        refetch={refetch}
        MessageAlert={MessageAlert}
      />
    </>
  );
};

export default UserDashboardContainer;
